import type React from 'react';
import { useCallback, useContext, useMemo } from 'react';

import { Box, useTheme } from '@mui/material';
import { Handle, Position, useNodeId, useStore } from '@xyflow/react';
import { shallow } from 'zustand/shallow';

import { edgeHighlightContext } from '../context/edgeHighlightContext';

import type { SortableInput } from '../dataProvider/types';

export interface NodeBodyProps {
  handles?: SortableInput[];
  type: string;
  backgroundColor?: string;
  children?: React.ReactNode;
  condensed?: boolean;
}

// 2px node border + 52px header + 12px body padding + half a row, then one row pitch (16px row + 8px gap) per step.

const firstHandleTop = 74;
const handlePitch = 24;

/**
 * Renders some content inside of the body of a Node component.
 *
 * @param {NodeHandle[]} handles - An array of Node handles ( applicable for multiple output Nodes ).
 * @param {string} type - The type of the Node.
 * @param {string} backgroundColor - The background color of the Node.
 * @param {React.ReactElement} children - The children elements of the Node.
 * @return {React.ReactElement} The rendered Node body.
 */
const NodeBody = ({
  handles = [],
  type,
  backgroundColor = undefined,
  children = null,
  condensed = false,
}: NodeBodyProps): React.ReactElement => {
  const nodeId = useNodeId();
  const { hoveredEdgeId } = useContext(edgeHighlightContext);
  const theme = useTheme();
  const activeHandles = useStore(
    useCallback(
      (state) => {
        if (nodeId === null) {
          return [];
        }

        return state.edges
          .filter(
            (edge) =>
              (edge.selected === true || edge.id === hoveredEdgeId) &&
              (edge.source === nodeId || edge.target === nodeId)
          )
          .map((edge) => (edge.source === nodeId ? edge.sourceHandle : edge.targetHandle))
          .filter((handleId): handleId is string => typeof handleId === 'string' && handleId.length > 0);
      },
      [hoveredEdgeId, nodeId]
    ),
    shallow
  );
  const activeHandleIds = useMemo(() => new Set(activeHandles), [activeHandles]);

  return (
    <Box
      sx={{
        backgroundColor,
        borderBottomLeftRadius: `calc(${theme.shape.borderRadius} - 2px)`,
        borderBottomRightRadius: `calc(${theme.shape.borderRadius} - 2px)`,
        paddingY: handles.length > 0 || children !== null ? theme.spacing(1.5) : 0,
        paddingX: theme.spacing(condensed ? 0.5 : 1.5),
        fontSize: '14px',
        lineHeight: 'normal',
      }}
      display='flex'
      flexDirection='column'
      gap={1}
    >
      {children}
      {handles?.map((handle, index) => {
        const isHandleActive = activeHandleIds.has(handle.id);
        const handleContainerClassName = `handle-container${isHandleActive ? ' handle-container--active' : ''}`;

        return (
          <Box key={handle.id} data-testid={`${type}-source-handle`} className={handleContainerClassName}>
            <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={handle.value}>
              {handle.value !== '' ? handle.value : '-'}
            </Box>
            <Handle
              type='source'
              position={Position.Right}
              id={handle.id}
              style={{ top: firstHandleTop + handlePitch * index }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default NodeBody;
